import { Link } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { useUserProfile } from '../context/UserProfileContext';

type LandingPageProps = {
  session: Session | null;
};

const LandingPage = ({ session }: LandingPageProps) => {
  const { profile } = useUserProfile();
  const primaryHref = session ? '/dashboard' : '/login';
  const primaryLabel = session ? 'Go to dashboard' : 'Login / Signup';
  const showAdminLink = profile?.role === 'admin';

  return (
    <div className="page">
      <header className="hero">
        <div className="hero-inner">
          <span className="eyebrow">
            <span className="material-symbols-rounded">bolt</span>
            Built by founders who needed proof yesterday
          </span>
          <h1>A calm surface to turn an idea into something you can demo.</h1>
          <p className="hero-lede">
            Open Shanework, sketch the data, and the endpoints are already there. Investors, teammates, and early users see progress instead
            of spreadsheets.
          </p>
          <p className="hero-lede">
            Toggle realtime, grab a prompt, and keep shipping without betting the entire stack. When the story changes, you can still move.
          </p>
          <div className="hero-cta">
            <Link className="cta cta-primary" to={primaryHref}>
              <span className="material-symbols-rounded">dashboard</span>
              {primaryLabel}
            </Link>
            {showAdminLink ? (
              <Link className="cta cta-secondary" to="/admin">
                <span className="material-symbols-rounded">shield_person</span>
                Admin
              </Link>
            ) : null}
          </div>
          <p className="hero-note">No tarballs. No CLIs. Just log in and shape data.</p>
        </div>
      </header>

      <main className="sections">
        <section className="problem-band" id="problem">
          <div className="problem-text">
            <p className="problem-label">The pause</p>
            <h2>Runway disappears while you debate infra.</h2>
            <p>Your instincts say “ship,” but the stack debate, auth scaffolding, and future scale worries keep the product on the whiteboard.</p>
          </div>
          <div className="solution-text">
            <p className="problem-label">What we give you</p>
            <p>
              A visible place to shape data, snippets you can paste, a live feed for signal, and prompt seeds to bootstrap UI. Nothing to
              install. Nothing you can’t unwind later.
            </p>
          </div>
        </section>

        <section className="section" id="story">
          <div className="section-heading">
            <h2>What’s on the bench</h2>
            <p>The bare minimum a scrappy team needs before the real build.</p>
          </div>
          <div className="story-cards">
            <article className="story-card">
              <h3>Data canvas</h3>
              <p>Draw the shape of your product in minutes. Columns, links, and sample rows show up without writing boilerplate.</p>
            </article>
            <article className="story-card">
              <h3>Copyable snippets</h3>
              <p>Grab curl, JavaScript, or Swift samples that already know your schema. Paste into slides, demos, or the real app.</p>
            </article>
            <article className="story-card">
              <h3>Live feed toggle</h3>
              <p>Flip a switch and show a live dashboard to a customer or investor. No extra wiring.</p>
            </article>
            <article className="story-card">
              <h3>Prompt seeds</h3>
              <p>Drop a vetted prompt into your favorite model so it scaffolds UI or agents against Shanework without guessing.</p>
            </article>
          </div>
        </section>

        <section className="section" id="workflow">
          <div className="section-heading">
            <h2>How a founder uses it</h2>
          </div>
          <div className="timeline" role="list">
            <article className="timeline-item" role="listitem">
              <span className="timeline-index">01</span>
              <div>
                <h3>Compose visually</h3>
                <p>Give the idea a name, sketch the entities, and hit save.</p>
              </div>
            </article>
            <article className="timeline-item" role="listitem">
              <span className="timeline-index">02</span>
              <div>
                <h3>Deploy without ceremony</h3>
                <p>Copy a snippet, call the endpoint, paste the response into your deck or build.</p>
              </div>
            </article>
            <article className="timeline-item" role="listitem">
              <span className="timeline-index">03</span>
              <div>
                <h3>Stay in sync</h3>
                <p>Flip on the live feed, share the link, and keep iterating in realtime.</p>
              </div>
            </article>
          </div>
        </section>

        <section className="section" id="promise">
          <div className="promise-panel">
            <div className="promise-copy">
              <h2>Momentum now, optionality later.</h2>
              <p>If the idea lands and you need a custom stack, we’ll show you how to take your data with you. Until then, keep the scoreboard moving.</p>
            </div>
            <blockquote>
              “This is what we wish we had when the board asked for a demo and the backend was still in our heads.”
            </blockquote>
          </div>
        </section>

        <section className="section login-section" id="login">
          <div className="section-heading">
            <h2>Sign in when you’re ready</h2>
            <p>Send yourself a code, step into the dashboard, and get back to building.</p>
            <div className="login-meta">
              <span>· Email-only codes, no passwords to remember.</span>
              <span>· Sessions scoped to your project so teams stay focused.</span>
              <span>· Step in now, step out later without friction.</span>
            </div>
          </div>
          <div className="login-actions">
            <Link className="cta cta-primary" to="/login">
              <span className="material-symbols-rounded">login</span>
              Login
            </Link>
            {session ? (
              <Link className="cta cta-secondary" to="/dashboard">
                <span className="material-symbols-rounded">dashboard</span>
                Go to dashboard
              </Link>
            ) : null}
          </div>
        </section>
      </main>

      <footer>
        <span>© Shanework · Built from 20 years of calm launches</span>
        <span className="footer-links">
          <Link to={session ? '/dashboard' : '/login'}>{session ? 'Dashboard' : 'Login'}</Link>
          <a href="README.md">Docs</a>
          <a href="#login">Login</a>
        </span>
      </footer>
    </div>
  );
};

export default LandingPage;

